import React, { useState } from 'react';
import { injectIntl } from 'react-intl';
import { i18nIntl } from '../../lib/ep.js';

import { MainButton, SecondaryButton } from '../builder/ActionButton';
import ActionLink from '../builder/ActionLink';
import ButtonCollection from '../builder/ButtonCollection';
import Input from '../builder/Input';
import FloatingLayer from '../builder/FloatingLayer';
import Model from '../builder/Model.js';
import SectionTitle from '../builder/SectionTitle';
import SectionSubtitle from '../builder/SectionSubtitle';
import ShareTestMessage from '../builder/ShareTestMessage.js';
import Tooltip from '../builder/Tooltip.js';

import FBShare from '../front/share/FB';
import TWShare from '../front/share/Twitter';
import PinShare from '../front/share/Pinterest';
import THShare from '../front/share/Threads';
import InShare from '../front/share/Linkedin';
import MailShare from '../front/share/SendByMail';
import LinkCollection from '../ui_items/LinkCollection.js';

const ShareSection = (props) => {
	const data_share = {
		promotion_id:props.promo.id,
		type:'promotion',
	};

	const sn_post = (props.promo.post_tweet) ? props.promo.post_tweet : '';
	const sn_img  = props?.promo?.full_post_image ?? (props?.promo?.full_image ?? false);

	const is_floating = (props.floating != undefined && props.floating) ? ' is_floating' : '';

	const is_test = window?.test ?? false;

	return (
		<ButtonCollection _class={{ other:`networks_buttons${is_floating}` }}>
			<FBShare
				linkable={!is_test}
				url={`${props.main_link}fb`}
				share={data_share}
				squared={true}
			/>

			<TWShare
				linkable={!is_test}
				url={`${props.main_link}tw`}
				share={data_share}
				post={sn_post}
				squared={true}
			/>

			<PinShare
				linkable={!is_test}
				url={`${props.main_link}pt`}
				share={data_share}
				post={sn_post}
				img={sn_img}
				squared={true}
			/>

			<InShare
				linkable={!is_test}
				url={`${props.main_link}ln`}
				share={data_share}
				post={sn_post}
				img={sn_img}
				squared={true}
			/>

			<THShare
				linkable={!is_test}
				url={`${props.main_link}th`}
				share={data_share}
				post={sn_post}
				squared={true}
			/>

			<MailShare
				linkable={!is_test}
				url={`${props.main_link}email`}
				share={data_share}
				subject={props.promo.title}
				post={props.promo.description_no_html}
				squared={true}
			/>
		</ButtonCollection>
	)
}

const PromoShareLayer = (props) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const [ display_utm_setup, setDisplayUTMSetup ] = useState(false);
	const [ utm_part, setUTMPart ] = useState('');
	const [ utms, setUTMs ] = useState({ source:'', medium:'', campaign:'' });

	const promo = props?.promotion ?? null;
	const languages = props?.languages ?? false;
	const main_link = props?.main_link ?? `${window.public_url}p/${promo.id}`;

	let is_test = window?.test ?? false;
	if(promo.test != undefined) is_test = promo.test;

	const customizeUTMs = (k, v) => {
		let _utms = {...utms, [k]:v};
		setUTMs(_utms);

		let _utms_new = [];
		if(_utms.source != '') _utms_new.push(`utm_source=${_utms.source}`);
		if(_utms.medium != '') _utms_new.push(`utm_medium=${_utms.medium}`);
		if(_utms.campaign != '') _utms_new.push(`utm_campaign=${_utms.campaign}`);
		
		setUTMPart(_utms_new.join("&"));
	};

	const addUTMsToURL = (loc = false, extra_get = '') => {
		let gets = [];
		if(loc !== false) gets.push(`lc=${loc}`);
		if(utm_part != '') gets.push(utm_part);
		if(extra_get != '') gets.push(extra_get);

		return `${main_link}?${gets.join("&")}`;
	};

	const main_link_utm = addUTMsToURL();

	let links = [{
		label:(promo.promo_type == '2') ? {
			children:"Direct link to this participation stage",
			tooltip:<Tooltip>If you share the main link, the system will try to detect automatically the language of the user through the browser's default language.</Tooltip>,
		} : {
			children:"Main link",
		},
		urls:[{
			url:main_link_utm,
		}],
		extra:<ShareSection
			promo={promo}
			main_link={addUTMsToURL(false, 'ep1=')}
		/>,
	}];

	if(promo.promo_type == '2' && languages !== false) {
		languages.map((l, i) => {
			const lang_link = addUTMsToURL(l.k);

			links.push({
				label:{
					autotranslate:false,
					_class:{other:'gui_flex_align_center'},
					children:`${i18n("Link in")} ${l.v.label}`,
				},
				urls:[{
					url:lang_link,
					_class:{ other:`is_${l.k}` },
				}],
				extra:<ShareSection
					promo={promo}
					main_link={addUTMsToURL(l.k, 'ep1=')}
				/>
			});
		})
	}

	return (
		<FloatingLayer backButton={props.backButton}>
			<div>
				<SectionTitle>Promotion link options</SectionTitle>

				<SectionSubtitle autotranslate={false}>{i18n("Use this link to share your promotion anywhere")} ({i18n("fully compatible with mobile devices")}).</SectionSubtitle>

				<ShareTestMessage
					is_test={is_test}
				/>

				<LinkCollection
					items={links}
					_class={(languages && languages.length > 0) ? { other:'gui_overflowed_auto' } : null}
				/>

				{(display_utm_setup) && (
					<aside
						class="gui_additional_floating_actions gui_only_wide_screen"
						onClick={(e) => {
							if(e.target != e.currentTarget) return;
							setDisplayUTMSetup(false);
						}}
					>
						<div>
							<SectionTitle>Campaign links builder</SectionTitle>

							<SectionSubtitle>Fill in the 3 parameters below to create URLs to your promotion for custom campaigns for entrants tracking</SectionSubtitle>

							<Model classname="gui_equal_columns gui_padded_children_low" model={utms} change={customizeUTMs}>
								<Input
									name="source"
									_class={{first:true}}
								>UTM source</Input>

								<Input
									name="medium"
									_class={{first:true}}
								>UTM medium</Input>

								<Input
									name="campaign"
									_class={{first:true}}
								>UTM campaign</Input>
							</Model>

							<ButtonCollection _class={{other:'not_in_top'}}>
								<SecondaryButton
									onClick={() => setDisplayUTMSetup(false)}
								>Cancel</SecondaryButton>

								<MainButton
									onClick={() => setDisplayUTMSetup(false)}
								>Apply</MainButton>
							</ButtonCollection>
						</div>
					</aside>
				)}

				<ButtonCollection in_floating={true} _class={{other:'gui_not_padded_in_top_small gui_not_padded_in_bottom_small'}}>
					<div>
						<p>{i18n("You can add UTM parameters to the link to measure the sources of the participants.")}</p>

						<ButtonCollection _class={{other:'gui_padded_children_low gui_flex_end'}}>
							<ActionLink _href={`${window.zendesk_base_url}207885366`} _target="_blank">More information</ActionLink>

							<ActionLink onClick={() => setDisplayUTMSetup(!display_utm_setup)}>UTMs Builder</ActionLink>
						</ButtonCollection>
					</div>
				</ButtonCollection>
			</div>
		</FloatingLayer>
	);
}

export default injectIntl(PromoShareLayer);